import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2, Upload } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const LOAN_TYPES = ["Personal Loan","Home Loan","Business Loan","MSME Loan","CV / CE Loan","Loan Against Property","CC / OD Facility"];

export default function AgentNewLead() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [d, setD] = useState({
    full_name:"", pan:"", mobile:"", email:"",
    employment_type:"Salaried", monthly_income:"", loan_amount:"",
    loan_type:"Personal Loan", tenure_months:60,
    document_name:"", document_base64:"", consent:true,
  });
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2_000_000) { toast.error("Max 2 MB"); return; }
    const r = new FileReader(); r.onload = () => { set("document_base64", String(r.result || "")); set("document_name", f.name); }; r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!d.full_name || !d.mobile || !d.email) { toast.error("Fill mandatory fields"); return; }
    setBusy(true);
    try {
      const phone = d.mobile.startsWith("+") ? d.mobile : `+91${d.mobile.replace(/\D/g,"")}`;
      await api.post("/agents/leads", {
        ...d, mobile: phone,
        monthly_income: Number(d.monthly_income) || 0,
        loan_amount: Number(d.loan_amount) || 0,
        tenure_months: Number(d.tenure_months) || 60,
      });
      setDone(true); toast.success("Lead submitted");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submit failed");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="card-flat p-10 max-w-md mx-auto text-center">
      <div className="mx-auto h-16 w-16 rounded-full bg-saffron/15 grid place-items-center"><CheckCircle2 className="h-8 w-8 text-saffron"/></div>
      <h2 className="font-display text-2xl font-bold mt-4">Lead submitted</h2>
      <div className="flex gap-3 justify-center mt-6">
        <Button variant="outline" className="rounded-full" onClick={() => nav("/agent/leads")}>View leads</Button>
        <Button className="btn-saffron rounded-full" onClick={() => { setDone(false); setD({ ...d, full_name:"", pan:"", mobile:"", email:"", monthly_income:"", loan_amount:"" }); }}>Add another</Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl">
      <div className="overline">Capture</div>
      <h1 className="font-display text-3xl font-extrabold mt-1">Submit a new lead</h1>

      <form onSubmit={submit} className="mt-6 card-flat p-7 space-y-5" data-testid="agent-new-lead-form">
        <div className="grid sm:grid-cols-2 gap-4">
          <F label="Customer name"><Input data-testid="al-name" value={d.full_name} onChange={e=>set("full_name", e.target.value)} required/></F>
          <F label="Loan type">
            <Select value={d.loan_type} onValueChange={v=>set("loan_type", v)}>
              <SelectTrigger data-testid="al-type"><SelectValue/></SelectTrigger>
              <SelectContent>{LOAN_TYPES.map(t=> <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Mobile"><Input data-testid="al-mobile" value={d.mobile} onChange={e=>set("mobile", e.target.value)} required/></F>
          <F label="Email"><Input data-testid="al-email" type="email" value={d.email} onChange={e=>set("email", e.target.value)} required/></F>
          <F label="PAN"><Input data-testid="al-pan" value={d.pan} onChange={e=>set("pan", e.target.value.toUpperCase())}/></F>
          <F label="Employment">
            <Select value={d.employment_type} onValueChange={v=>set("employment_type", v)}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent><SelectItem value="Salaried">Salaried</SelectItem><SelectItem value="Self-Employed">Self-Employed</SelectItem></SelectContent>
            </Select>
          </F>
          <F label="Monthly income (₹)"><Input data-testid="al-income" type="number" value={d.monthly_income} onChange={e=>set("monthly_income", e.target.value)}/></F>
          <F label="Loan amount (₹)"><Input data-testid="al-amount" type="number" value={d.loan_amount} onChange={e=>set("loan_amount", e.target.value)}/></F>
          <F label="Tenure (months)"><Input data-testid="al-tenure" type="number" value={d.tenure_months} onChange={e=>set("tenure_months", e.target.value)}/></F>
        </div>

        <F label="Optional document upload">
          <label className="flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-2xl p-5 cursor-pointer hover:border-saffron hover:bg-saffron/5">
            <Upload className="h-5 w-5 text-saffron"/>
            <span className="text-sm">{d.document_name || "Choose file (PDF/JPG/PNG, max 2 MB)"}</span>
            <input data-testid="al-doc" type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onFile}/>
          </label>
        </F>

        <label className="flex items-start gap-3">
          <Checkbox checked={d.consent} onCheckedChange={(v)=>set("consent", !!v)} className="mt-0.5"/>
          <span className="text-xs text-ink-muted">Customer has consented to share details with TARA Finserv lending partners.</span>
        </label>

        <Button data-testid="al-submit" type="submit" disabled={busy} className="btn-saffron rounded-full h-12 px-7">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>} Submit lead
        </Button>
      </form>
    </div>
  );
}

function F({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
